import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import LoadingIndicator from 'primitives/LoadingIndicator';

const AUTHENTICATE_GOOGLE = gql(`
  mutation authenticateGoogle($code: String!) {
    authenticateGoogle(code: $code)
  }
`);

const GoogleCallback: FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [authenticateGoogle] = useMutation(AUTHENTICATE_GOOGLE);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');

        if (!code) {
          setStatus('error');
          setErrorMessage('No authentication code found in URL');
          return;
        }

        // Send the code to the parent window
        if (window.opener) {
          window.opener.postMessage({ type: 'GOOGLE_AUTH_CODE', code }, '*');
          // Close this window after sending the message
          window.close();
          return;
        }

        // If there's no opener (direct navigation), handle the auth here
        const result = await authenticateGoogle({ variables: { code } });

        if (result.data?.authenticateGoogle) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage('Authentication failed');
        }
      } catch (error: any) {
        setStatus('error');
        setErrorMessage(error.message || 'Authentication failed');
      }
    };

    handleCallback();
  }, [searchParams, authenticateGoogle]);

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <LoadingIndicator />
        <p className="mt-4 text-gray-600">Authenticating with Google...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative max-w-md w-full"
          role="alert"
        >
          <p className="font-bold">Authentication Error</p>
          <p className="text-sm">{errorMessage || 'An unknown error occurred'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div
        className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative max-w-md w-full"
        role="alert"
      >
        <p className="font-bold">Authentication Successful</p>
        <p className="text-sm">
          You have successfully authenticated with Google. You can close this window now.
        </p>
      </div>
    </div>
  );
};

export default GoogleCallback;
